import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Star, Send, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use the correct backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const FeedbackSection = () => {
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    feedback: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please provide a rating');
      return;
    }

    if (!formData.feedback.trim()) {
      toast.error('Please write your feedback');
      return;
    }

    if (!formData.name) {
      toast.error('Please provide your name');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/feedback`, {
        name: formData.name,
        rating: rating,
        feedback: formData.feedback
      });
      
      if (response.data.success) {
        toast.success('Thank you for your feedback! We appreciate your time.');
        
        // Reset form
        setRating(0);
        setFormData({
          name: '',
          feedback: ''
        });

        // Redirect to home page after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        toast.error('Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error('Failed to submit feedback. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="feedback" className="py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.2em]">
              Share Your Experience
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white mb-4 tracking-tight">
            Rate Your Journey
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Your feedback helps us serve you better. Share your experience with SG Cabs!
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Card className="lux-card rounded-2xl shadow-none">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Star Rating */}
                <div className="text-center pb-6 border-b border-gray-200">
                  <Label className="text-lg font-semibold text-white mb-4 block">
                    How was your experience?
                  </Label>
                  <div className="flex items-center justify-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        data-testid={`feedback-star-${star}`}
                        className="transition-transform hover:scale-110 focus:outline-none"
                      >
                        <Star
                          className={`w-12 h-12 ${
                            star <= (hoveredRating || rating)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          } transition-colors`}
                        />
                      </button>
                    ))}
                  </div>
                  {rating > 0 && (
                    <p className="mt-4 text-[#d4af37] font-semibold">
                      {rating === 5 && 'Excellent! 🌟'}
                      {rating === 4 && 'Great! 👍'}
                      {rating === 3 && 'Good 😊'}
                      {rating === 2 && 'Okay 🙂'}
                      {rating === 1 && 'We can improve 💪'}
                    </p>
                  )}
                </div>

                {/* Personal Details */}
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="feedback-name" className="text-gray-300 font-semibold">
                      Your Name *
                    </Label>
                    <Input
                      id="feedback-name"
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37]"
                      required
                    />
                    <p className="text-sm text-gray-500">
                      We value your feedback and would love to know how we can serve you better
                    </p>
                  </div>
                </div>

                {/* Feedback Text */}
                <div className="space-y-2">
                  <Label htmlFor="feedback-text" className="text-gray-300 font-semibold">
                    Your Feedback *
                  </Label>
                  <Textarea
                    id="feedback-text"
                    placeholder="Tell us about your experience with SG Cabs..."
                    value={formData.feedback}
                    onChange={(e) => handleChange('feedback', e.target.value)}
                    className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37] min-h-32"
                    required
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold text-lg py-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                </div>
              </form>

              {/* Direct Link Info */}
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-400 mb-3">
                  Share this feedback link with your customers:
                </p>
                <div className="bg-[#1a1a1a] rounded-lg p-4 border border-white/10">
                  <code className="text-[#d4af37] font-mono text-sm break-all">
                    https://sg-cabs.com/feedback
                  </code>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  <ThumbsUp className="w-4 h-4 inline mr-1" />
                  Send this link to customers via SMS or WhatsApp after their journey
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="lux-card rounded-2xl">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-[#d4af37] mb-2">4.9/5</div>
                <div className="text-gray-400 font-medium">Average Rating</div>
                <div className="flex items-center justify-center mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="lux-card rounded-2xl">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-[#d4af37] mb-2">10,000+</div>
                <div className="text-gray-400 font-medium">Happy Customers</div>
              </CardContent>
            </Card>

            <Card className="lux-card rounded-2xl">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-[#d4af37] mb-2">98%</div>
                <div className="text-gray-400 font-medium">Satisfaction Rate</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
